/* import __COLOCATED_TEMPLATE__ from './eisa-scoring.hbs'; */
import Component from '@glimmer/component';

const pointsEISA = [50, 47, 44, 41, 39, 37, 35, 33, 31, 30, 29, 28, 27, 26, 25, 24, 23, 22, 21, 20, 19, 18, 17, 16, 15, 14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1];

export default class EisaScoringComponent extends Component
{
	// Nordic teams
	get nordicTeams()
	{
		return this.args.config?.nordic?.teams || [];
	}

	get nordicScoringTeams()
	{
		return this.args.config?.nordic?.scoringTeams || [];
	}

	// Alpine teams
	get alpineTeams()
	{
		return this.args.config?.alpine?.teams || [];
	}

	get alpineScoringTeams()
	{
		return this.args.config?.alpine?.scoringTeams || [];
	}

	get nordicRaces()
	{
		return this.args.config?.nordic?.races || [];
	}

	get alpineRaces()
	{
		return this.args.config?.alpine?.races || [];
	}

	scoreNordicRace(raceConfig)
	{
		const allResults = this.args.results?.[raceConfig.resultsFile] || [];
		// Filter by class group (use loose equality for string/number compatibility)
		const classResults = allResults.filter(r => r.ClassGroupId == raceConfig.classGroupId);

		// Filter to all EISA teams with OK status and a finish time, sorted by original place
		const eisaResults = classResults.filter(r =>
			this.nordicTeams.includes(r.club) && r.status === 'OK' && r.totalTimeInMilliseconds
		).sort((a, b) => a.place - b.place);

		return this.assignPointsAndScore(eisaResults, this.nordicTeams, this.nordicScoringTeams);
	}

	scoreAlpineRace(raceConfig)
	{
		// Alpine results come from parsed XML, already filtered to finishers
		const allResults = this.args.results?.[raceConfig.resultsFile] || [];

		// Filter to EISA teams, sorted by place
		const eisaResults = allResults.filter(r =>
			this.alpineTeams.includes(r.club)
		).sort((a, b) => a.place - b.place);

		return this.assignPointsAndScore(eisaResults, this.alpineTeams, this.alpineScoringTeams);
	}

	assignPointsAndScore(eisaResults, teams, scoringTeams)
	{
		// Assign EISA place
		eisaResults.forEach((r, i) => r.eisaPlace = i + 1);

		// Track how many scorers each team has (max 3 can score points)
		const teamScorerCount = {};

		// Non-scoring team athletes and 4th+ athletes from a team don't consume points
		let pointIndex = 0;
		let i = 0;
		while (i < eisaResults.length)
		{
			// Find all athletes with the same ORIGINAL place (they were tied)
			let j = i;
			while (j < eisaResults.length && eisaResults[j].place === eisaResults[i].place)
			{
				j++;
			}
			// i to j-1 are tied (same original place)
			// Find athletes eligible for points (scoring team + not already 3 scorers from that team)
			const tiedScoringAthletes = [];
			for (let k = i; k < j; k++)
			{
				const club = eisaResults[k].club;
				if (scoringTeams.includes(club) && (teamScorerCount[club] || 0) < 3)
				{
					tiedScoringAthletes.push(eisaResults[k]);
				}
				else
				{
					eisaResults[k].eisaPoints = 0;
				}
			}

			// Assign averaged points to the tied scoring athletes
			if (tiedScoringAthletes.length > 0)
			{
				let totalPoints = 0;
				for (let k = 0; k < tiedScoringAthletes.length; k++)
				{
					totalPoints += pointsEISA[pointIndex + k] || 0;
				}
				const avgPoints = totalPoints / tiedScoringAthletes.length;
				for (const athlete of tiedScoringAthletes)
				{
					athlete.eisaPoints = avgPoints;
					teamScorerCount[athlete.club] = (teamScorerCount[athlete.club] || 0) + 1;
				}
				pointIndex += tiedScoringAthletes.length;
			}

			i = j;
		}

		// Group by team for display (only scoring teams, max 3)
		const teamScorers = {};
		for (const r of eisaResults)
		{
			if (!scoringTeams.includes(r.club)) continue;
			if (!teamScorers[r.club]) teamScorers[r.club] = [];
			if (teamScorers[r.club].length < 3)
			{
				teamScorers[r.club].push(r);
			}
		}

		// Calculate team totals
		const teamScores = {};
		for (const [team, scorers] of Object.entries(teamScorers))
		{
			teamScores[team] = scorers.reduce((sum, r) => sum + r.eisaPoints, 0);
		}

		return { scorers: eisaResults, teamScorers, teamScores };
	}

	get nordicScores()
	{
		return this.nordicRaces.map((race, index) => ({
			...race,
			raceIndex: index,
			...this.scoreNordicRace(race)
		}));
	}

	get alpineScores()
	{
		const nordicCount = this.nordicRaces.length;
		return this.alpineRaces.map((race, index) => ({
			...race,
			raceIndex: nordicCount + index,
			...this.scoreAlpineRace(race)
		}));
	}

	get teamTotals()
	{
		const totals = {};

		// Initialize all scoring teams (union of nordic and alpine scoring teams)
		const allScoringTeams = new Set([...this.nordicScoringTeams, ...this.alpineScoringTeams]);
		for (const team of allScoringTeams)
		{
			totals[team] = {
				name: team,
				nordicDay1Men: 0,
				nordicDay1Women: 0,
				nordicDay2Men: 0,
				nordicDay2Women: 0,
				nordicTotal: 0,
				alpineGSMen: 0,
				alpineGSWomen: 0,
				alpineSLMen: 0,
				alpineSLWomen: 0,
				alpineTotal: 0,
				carnivalTotal: 0
			};
		}

		// Accumulate scores from each nordic race
		for (const race of this.nordicScores)
		{
			const isMen = race.gender === 'M';
			const isDay1 = race.day === 1;
			for (const [team, score] of Object.entries(race.teamScores || {}))
			{
				if (totals[team])
				{
					if (isMen && isDay1)
					{
						totals[team].nordicDay1Men += score;
					}
					else if (!isMen && isDay1)
					{
						totals[team].nordicDay1Women += score;
					}
					else if (isMen && !isDay1)
					{
						totals[team].nordicDay2Men += score;
					}
					else
					{
						totals[team].nordicDay2Women += score;
					}
				}
			}
		}

		// Accumulate scores from each alpine race
		for (const race of this.alpineScores)
		{
			const isMen = race.gender === 'M';
			const isGS = race.discipline === 'GS';
			for (const [team, score] of Object.entries(race.teamScores || {}))
			{
				if (totals[team])
				{
					if (isMen && isGS)
					{
						totals[team].alpineGSMen += score;
					}
					else if (!isMen && isGS)
					{
						totals[team].alpineGSWomen += score;
					}
					else if (isMen && !isGS)
					{
						totals[team].alpineSLMen += score;
					}
					else
					{
						totals[team].alpineSLWomen += score;
					}
				}
			}
		}

		// Calculate totals
		for (const team of Object.values(totals))
		{
			team.nordicTotal = team.nordicDay1Men + team.nordicDay1Women + team.nordicDay2Men + team.nordicDay2Women;
			team.alpineTotal = team.alpineGSMen + team.alpineGSWomen + team.alpineSLMen + team.alpineSLWomen;
			team.carnivalTotal = team.nordicTotal + team.alpineTotal;
		}

		// Convert to sorted array (by carnival total descending)
		return Object.values(totals)
			.filter(t => t.carnivalTotal > 0)
			.sort((a, b) => b.carnivalTotal - a.carnivalTotal);
	}

	// Helper getters for displaying races by gender
	get nordicMenRaces()
	{
		return this.nordicScores.filter(r => r.gender === 'M');
	}

	get nordicWomenRaces()
	{
		return this.nordicScores.filter(r => r.gender === 'F');
	}

	get alpineMenRaces()
	{
		return this.alpineScores.filter(r => r.gender === 'M');
	}

	get alpineWomenRaces()
	{
		return this.alpineScores.filter(r => r.gender === 'F');
	}
}
